import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Text, Anchor } from 'grommet';
import * as Sentry from '@sentry/browser';
import { UserContext } from '@services/Session';
import { AppHostContext } from '@services/appHost';
import { useAuthProviderInfo } from '../SignIn/authHooks';
import { AuthProviders } from '../SignIn/signin.types';
import useAccountInfo from './hooks/useAccountInfo';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDCheckbox from '@ui/Checkbox/SHDCheckbox';
import SHDInput from '@ui/Input/SHDInput';
import SHDInputPhoneNumber from '@ui/InputPhoneNumber/SHDInputPhoneNumber';
import SHDButton from '@ui/Button/SHDButton';
import { strogging } from '@shd/jslib/infra';
import type { ErrorMessage } from './SignIn.d';
import { isValidPhoneNumber } from 'react-phone-number-input';

interface Props {
  inviteCode?: string | undefined;
  redirectUrl?: string | undefined;
  onCollectionComplete?: () => void;
  fromInvite?: boolean;
}
const AccountInfo: React.FC<Props> = ({
  inviteCode,
  redirectUrl,
  fromInvite,
  onCollectionComplete,
}) => {
  const userStore = useContext(UserContext);
  const appHost = useContext(AppHostContext);
  const { providers } = useAuthProviderInfo();
  const history = useHistory();
  const { validateUserInfo } = useAccountInfo();

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<ErrorMessage | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [emailError, setEmailError] = useState<{ message: string } | null>(
    null
  );

  const [email, setEmail] = useState<string>(userStore.authUser?.email || '');
  const [error, setError] = useState<ErrorMessage | null>();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  const isApple = providers.find((obj) => obj.id === AuthProviders.APPLE)
    ? true
    : false;

  const emailRequired = !userStore.user.email && !isApple;
  const phoneRequired = !userStore.userPhone;

  let tokenizedName;

  if (userStore.authUser?.displayName) {
    tokenizedName = userStore.authUser.displayName.split(' ');
  } else {
    tokenizedName = [
      userStore.user.nameFirst || '',
      userStore.user.nameLast || '',
    ];
  }

  const redirectToExternal = (url: string) => {
    const appHostService = appHost.isHosted ? appHost.service : undefined;
    if (appHost.isHosted) {
      appHostService?.notifications.openAppLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const [nameFirst, setNameFirst] = useState(
    tokenizedName.length > 0 ? tokenizedName[0] : ''
  );
  const [nameLast, setNameLast] = useState(
    tokenizedName.length > 1 ? tokenizedName[1] : ''
  );

  // Handle button enable/disable
  useEffect(() => {
    if (emailRequired && isChecked && emailIsValid && email !== '') {
      setButtonDisabled(false);
      return;
    }

    if (phoneRequired && isChecked && phoneIsValid && phoneNumber !== '') {
      setButtonDisabled(false);
      return;
    }

    if (!emailRequired && !phoneRequired && isChecked) {
      setButtonDisabled(false);
      return;
    }

    setButtonDisabled(true);
  }, [
    isChecked,
    email,
    emailIsValid,
    emailRequired,
    phoneRequired,
    phoneNumber,
    phoneIsValid,
  ]);

  useEffect(() => {
    if (userStore.userPhone) {
      setPhoneIsValid(true);
    }
  }, [userStore.userPhone, phoneIsValid]);

  const validatePhoneNumber = () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      setPhoneIsValid(false);
      setPhoneError({ message: 'Please enter a valid phone number.' });
      return;
    } else {
      setPhoneIsValid(true);
      setPhoneError(null);
    }
  };

  // Email validation function
  const validateEmail = (isValid: boolean) => {
    if (!isValid) {
      setEmailIsValid(false);
      setEmailError({ message: 'Please enter a valid email address.' });
    } else {
      setEmailIsValid(true);
      setEmailError(null);
    }
  };

  const validate = (playerClaim = false) => {
    validateUserInfo({
      nameFirst,
      nameLast,
      phoneNumber,
      email,
      inviteCode,
      tosAccepted: isChecked,
    })
      .then(() => {
        if (phoneNumber) {
          userStore.setUserPhone(phoneNumber);
        }
        if (playerClaim) {
          onCollectionComplete && onCollectionComplete();
          history.push('/claim_player');
          return;
        }
        onCollectionComplete && onCollectionComplete();
        const continueUrl = redirectUrl || '';
        redirectUrl && history.push(continueUrl);
      })
      .catch((respError) => {
        Sentry.captureException(respError);
        setError({
          message: respError.response?.data?.message || 'An error occurred',
        });
      });
  };
  const nextButtonProps = {
    onError: (err: ErrorMessage) => {
      strogging.log(err.message), setError(err);
    },
    trackingEvent: fromInvite
      ? 'Continue clicked'
      : 'Start team creation clicked',
    trackingContext: fromInvite ? 'Continue' : 'Start team creation',
    className: 'StartTeamCreation',
    primary: true,
    label: fromInvite ? 'Continue' : 'Start team creation',
    onClick: validate,
    disabled: buttonDisabled,
  };

  const checkBoxLabel = !userStore.userPhone ? (
    <Text color="#000000" size="small" wordBreak="break-word">
      I agree to the Terms of Service, Privacy Policy, and End User License
      Agreement, and I consent to sidelineHD sending SMS messages for login
      support, delivery of highlight videos, and livestream status updates.
    </Text>
  ) : (
    <Text color="#000000" size="small" wordBreak="break-word">
      I agree to Diamond Kinetics&apos;{' '}
      <Anchor
        size="small"
        label="Privacy Policy"
        onClick={() => redirectToExternal('https://sidelinehd.com/privacy')}
      />
      <Anchor
        size="small"
        label="Terms of Service"
        href="https://sidelinehd.com/terms-of-service"
        target="_blank"
        onClick={() =>
          redirectToExternal('https://sidelinehd.com/terms-of-service')
        }
      />
      <Anchor
        size="small"
        label="End User License Agreement"
        href="https://sidelinehd.com/eula"
        onClick={() => redirectToExternal('https://sidelinehd.com/eula')}
      />
      .
    </Text>
  );

  return (
    <Box width="100%" gap="small">
      <SHDHeading className="test" level="3">
        Let&apos;s get to know you
      </SHDHeading>

      <Box gap="xxsmall" margin={{ bottom: 'small' }}>
        <SHDInput
          autoComplete="given-name"
          value={nameFirst === 'Unknown' ? '' : nameFirst}
          onChange={(val) => setNameFirst(val)}
          label={!isApple ? 'First Name' : 'First Name (Optional)'}
        />
        <SHDInput
          autoComplete="family-name"
          value={nameLast === 'Signin' ? '' : nameLast}
          onChange={(val) => setNameLast(val)}
          label={!isApple ? 'Last Name' : 'Last Name (Optional)'}
        />
      </Box>
      <Box gap="xxsmall" margin={{ bottom: 'small' }}>
        {error ? <Text color="status-critical">{error.message}</Text> : null}
        {phoneRequired && (
          <>
            <SHDInputPhoneNumber
              className="AccountInfoPhoneNumber"
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              error={phoneError}
              onChange={(value: string) => {
                setPhoneIsValid(false);
                setPhoneError(null);
                setPhoneNumber(value);
              }}
              onFocusOut={validatePhoneNumber}
            />
          </>
        )}
        {emailRequired && (
          <>
            <SHDInput
              autoComplete="email"
              value={email}
              onChange={(val) => {
                setEmailIsValid(false);
                setEmailError(null);
                setEmail(val);
              }}
              onFocusOut={validateEmail}
              label="Email"
              error={emailError}
            />
          </>
        )}
      </Box>
      <Box
        background="gray-50"
        gap="small"
        height={{ min: 'fit-content' }}
        pad={{ horizontal: 'medium', vertical: 'small' }}
        round="small"
        width="100%"
      >
        {phoneRequired && (
          <Text style={{ color: 'rgba(0,0,0,0.5)' }} size="small">
            We may send SMS messages when you log in to our app or website, when
            your athlete or team is playing, or when video highlights are ready.
            For HELP, visit{' '}
            <a
              href="https://help.sidelineHD.com"
              style={{ color: 'rgba(0,0,0,0.5)' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              help.sidelineHD.com
            </a>
            . To unsubscribe, send STOP. Standard message and data rates may
            apply. By consenting, you agree to Diamond Kinetics’{' '}
            <Anchor
              size="small"
              style={{ color: 'rgba(0,0,0,0.5)' }}
              label="Privacy Policy"
              onClick={() =>
                redirectToExternal('https://sidelinehd.com/privacy')
              }
            />
            {', '}
            <Anchor
              size="small"
              style={{ color: 'rgba(0,0,0,0.5)' }}
              label="Terms of Service"
              target="_blank"
              onClick={() =>
                redirectToExternal('https://sidelinehd.com/terms-of-service')
              }
            />
            {', '}
            <Anchor
              size="small"
              label="End User License Agreement"
              style={{ color: 'rgba(0,0,0,0.5)' }}
              onClick={() => redirectToExternal('https://sidelinehd.com/eula')}
            />
            .
          </Text>
        )}
        <SHDCheckbox
          className="checkbox-test"
          label={checkBoxLabel}
          checked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
        />
      </Box>
      <Box margin={{ top: 'xxsmall' }} gap="small">
        <SHDButton {...nextButtonProps} />
        {!fromInvite && (
          <SHDButton
            className="claim_player_button"
            onError={(err) => setError(err)}
            trackingEvent="Claim player"
            trackingContext="Claim player clicked from account info"
            label={
              <Text color="primary" size="small">
                Claim a player instead
              </Text>
            }
            disabled={buttonDisabled}
            onClick={() => validate(true)}
          />
        )}
      </Box>
    </Box>
  );
};

export default AccountInfo;
